import { useRef, useState, type ReactNode } from 'react';
import type {
  FinanceAttachmentSummary,
  ReceiptOrderRecord,
  ReceiptOrderTransitionRecord,
} from '@nadoutong/contracts';
import { Download, Paperclip, Upload } from 'lucide-react';
import {
  downloadReceiptAttachment,
  useReceiptAttachments,
  useReceiptDetail,
  useReceiptInvoiceAction,
  useUploadReceiptAttachment,
  type ReceiptInvoiceAction,
} from '../api/receipts.ts';
import { OpsApiError } from '../api/client.ts';
import {
  RECEIPT_FINANCE_DECISION_ROLES,
  hasAnyOpsRole,
} from '../auth/opsRoles.ts';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import {
  BRAND_INVOICE_MODE_LABEL_KEYS,
  RECEIPT_CHANNEL_LABEL_KEYS,
  RECEIPT_INVOICE_STATUS_LABEL_KEYS,
  RECEIPT_STATUS_LABEL_KEYS,
  attachmentMaxMbLabel,
  base64ToBytes,
  bytesToBase64,
  formatReceiptMinor,
  formatTaxPointBps,
  isOversizeAttachment,
  receiptInvoiceStatusChipTone,
  receiptStatusChipTone,
} from '../lib/receipts.ts';
import { CustomerRef } from './identity/CustomerRef.tsx';
import { OpsUserRef } from './identity/OpsUserRef.tsx';
import { formatUsdMicros, formatCnyCents, formatLocalTimestamp } from './formatters.ts';
import { coerceErrorMessage, useToast } from './Toast.tsx';
import {
  Button,
  Chip,
  Dialog,
  Drawer,
  EmptyState,
  FormField,
  Skeleton,
  Textarea,
} from '../ui/index.ts';

/**
 * Receipt detail sheet (epic #1031 E2-R5, #1063): status + the full
 * transition timeline (the synchronous `receipt_order_transitions`
 * history — never derived client-side), the credit-time stamps and FX
 * snapshot, and the evidence attachment list with upload / download.
 *
 * Everything money is a server fact rendered verbatim: amount/fee in
 * the receipt currency, the FX snapshot (applied policy rate, credited
 * USD, CNY gross/net) only once the credit batch wrote it. Attachment
 * upload re-checks the ~5 MB cap client-side for a friendly error; the
 * BFF and platform re-enforce it.
 */

interface ReceiptDetailDrawerProps {
  readonly receiptId: string | null;
  readonly onOpenChange: (open: boolean) => void;
}

export function ReceiptDetailDrawer({
  receiptId,
  onOpenChange,
}: ReceiptDetailDrawerProps): JSX.Element {
  const t = useT();
  const detail = useReceiptDetail(receiptId);

  return (
    <Drawer
      open={receiptId !== null}
      onOpenChange={onOpenChange}
      title={t('receipts.detail.title')}
      description={
        receiptId !== null ? <span className="font-mono">{receiptId}</span> : undefined
      }
      width="lg"
      closeLabel={t('common.close')}
      testId="receipt-detail-drawer"
    >
      {detail.isPending && receiptId !== null ? (
        <div className="flex flex-col gap-3" data-testid="receipt-detail-loading">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : null}
      {detail.isError ? (
        <EmptyState
          title={t('receipts.detail.error')}
          hint={coerceErrorMessage(detail.error)}
          action={
            <Button onClick={() => void detail.refetch()}>{t('common.retry')}</Button>
          }
          testId="receipt-detail-error"
        />
      ) : null}
      {detail.data !== undefined && receiptId !== null ? (
        <div className="flex flex-col gap-5">
          <ReceiptFacts receipt={detail.data.receipt} />
          {detail.data.receipt.status === 'credited' ? (
            <FxSnapshot receipt={detail.data.receipt} />
          ) : null}
          {detail.data.receipt.need_invoice ? (
            <InvoiceSection receipt={detail.data.receipt} />
          ) : null}
          <TransitionTimeline transitions={detail.data.transitions} />
          <AttachmentsSection receiptId={receiptId} />
        </div>
      ) : null}
    </Drawer>
  );
}

function Fact({
  label,
  value,
  mono = false,
  testId,
}: {
  readonly label: string;
  readonly value: ReactNode;
  readonly mono?: boolean;
  readonly testId?: string;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-label text-paper-low">{label}</span>
      <span
        className={mono ? 'break-all font-mono text-dense text-paper' : 'text-body text-paper'}
        data-testid={testId}
      >
        {value}
      </span>
    </div>
  );
}

function ReceiptFacts({ receipt }: { readonly receipt: ReceiptOrderRecord }): JSX.Element {
  const t = useT();
  return (
    <section className="flex flex-col gap-3" data-testid="receipt-detail-facts">
      <div className="flex flex-wrap items-center gap-2">
        <Chip tone={receiptStatusChipTone(receipt.status)} testId="receipt-detail-status">
          {t(RECEIPT_STATUS_LABEL_KEYS[receipt.status])}
        </Chip>
        <span className="text-label text-paper-low">{receipt.brand}</span>
      </div>
      <div className="grid gap-3 rounded-lg border border-line-soft bg-well/50 p-4 sm:grid-cols-2">
        <Fact
          label={t('receipts.detail.amount')}
          value={`${formatReceiptMinor(receipt.amount_minor, receipt.currency)} ${receipt.currency}`}
          testId="receipt-detail-amount"
        />
        <Fact
          label={t('receipts.detail.fee')}
          value={`${formatReceiptMinor(receipt.actual_fee_minor, receipt.currency)} ${receipt.currency}`}
        />
        <Fact
          label={t('receipts.detail.channel')}
          value={t(RECEIPT_CHANNEL_LABEL_KEYS[receipt.channel])}
        />
        <Fact
          label={t('receipts.detail.received_at')}
          value={formatLocalTimestamp(receipt.received_at)}
        />
        <Fact
          label={t('receipts.detail.contract_ref')}
          value={receipt.contract_ref ?? '—'}
          mono={receipt.contract_ref !== null}
        />
        <Fact
          label={t('receipts.detail.company')}
          value={receipt.customer_company ?? t('receipts.customer.company_missing')}
        />
        <Fact label={t('receipts.detail.org')} value={<CustomerRef orgId={receipt.org_id} />} mono />
        <Fact
          label={t('receipts.detail.entered_by')}
          value={<OpsUserRef id={receipt.entered_by_ops_user_id} />}
          mono
        />
        <Fact
          label={t('receipts.detail.submitted_at')}
          value={formatLocalTimestamp(receipt.submitted_at)}
        />
      </div>
    </section>
  );
}

/**
 * Credit-time stamps + FX snapshot — rendered only for credited
 * receipts, straight from the atomically written columns (#1061).
 */
function FxSnapshot({ receipt }: { readonly receipt: ReceiptOrderRecord }): JSX.Element {
  const t = useT();
  return (
    <section className="flex flex-col gap-2" data-testid="receipt-detail-fx">
      <h3 className="m-0 text-section text-paper">{t('receipts.detail.fx.title')}</h3>
      <div className="grid gap-3 rounded-lg border border-ok-line bg-ok-dim/30 p-4 sm:grid-cols-2">
        <Fact
          label={t('receipts.detail.fx.applied_rate')}
          value={receipt.applied_rate !== null ? String(receipt.applied_rate) : '—'}
          mono
          testId="receipt-detail-fx-rate"
        />
        <Fact
          label={t('receipts.detail.fx.credited_usd')}
          value={
            receipt.credited_usd_micros !== null
              ? formatUsdMicros(receipt.credited_usd_micros, false)
              : '—'
          }
          testId="receipt-detail-fx-credited-usd"
        />
        <Fact
          label={t('receipts.detail.fx.cny_gross')}
          value={receipt.cny_gross_cents !== null ? formatCnyCents(receipt.cny_gross_cents, false) : '—'}
        />
        <Fact
          label={t('receipts.detail.fx.cny_net')}
          value={receipt.cny_net_cents !== null ? formatCnyCents(receipt.cny_net_cents, false) : '—'}
        />
        <Fact
          label={t('receipts.detail.fx.verified_by')}
          value={<OpsUserRef id={receipt.verified_by_ops_user_id} />}
          mono
        />
        <Fact
          label={t('receipts.detail.fx.owner_at_credit')}
          value={<OpsUserRef id={receipt.owner_ops_user_id} />}
          mono
          testId="receipt-detail-fx-owner"
        />
        <Fact
          label={t('receipts.detail.fx.credited_at')}
          value={formatLocalTimestamp(receipt.credited_at)}
        />
        <Fact
          label={t('receipts.detail.fx.balance_tx')}
          value={receipt.balance_tx_id ?? '—'}
          mono
        />
      </div>
    </section>
  );
}

const INVOICE_ACTION_TITLE_KEYS: Record<ReceiptInvoiceAction, I18nKey> = {
  issue: 'receipts.invoice.issue_title',
  void: 'receipts.invoice.void_title',
};

const INVOICE_ACTION_LABEL_KEYS: Record<ReceiptInvoiceAction, I18nKey> = {
  issue: 'receipts.invoice.action.issue',
  void: 'receipts.invoice.action.void',
};

const INVOICE_ACTION_TOAST_KEYS: Record<ReceiptInvoiceAction, I18nKey> = {
  issue: 'receipts.invoice.toast.issued',
  void: 'receipts.invoice.toast.voided',
};

/**
 * Invoice block (#1064): the sales-declared need + tax point and the
 * finance `open → issued | voided` workflow. Issue/void render only
 * for finance-decision roles on an OPEN invoice of a CREDITED receipt —
 * the server refuses invoice decisions on uncredited receipts
 * (`RECEIPT_ORDER_NOT_CREDITED`), so the UI mirrors that authority and
 * shows an await-credit hint instead; the void confirm requires a note
 * (it lands in `ops_audit`). Losing the decide race (409
 * `RECEIPT_INVOICE_NOT_OPEN`) is not an error toast — the shared
 * invalidation refreshes the drawer to the true invoice state.
 */
function InvoiceSection({ receipt }: { readonly receipt: ReceiptOrderRecord }): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { user } = useSession();
  const canDecide = hasAnyOpsRole(user?.roles ?? [], RECEIPT_FINANCE_DECISION_ROLES);
  const actionable = receipt.invoice_status === 'open' && receipt.status === 'credited';
  const act = useReceiptInvoiceAction();
  const [action, setAction] = useState<ReceiptInvoiceAction | null>(null);
  const [note, setNote] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  function openAction(next: ReceiptInvoiceAction): void {
    setNote('');
    setActionError(null);
    setAction(next);
  }

  function confirmAction(): void {
    if (action === null || act.isPending) {
      return;
    }
    const trimmed = note.trim();
    if (action === 'void' && trimmed.length === 0) {
      return;
    }
    setActionError(null);
    act.mutate(
      { id: receipt.id, action, note: trimmed.length > 0 ? trimmed : null },
      {
        onSuccess: () => {
          setAction(null);
          toast.success(t(INVOICE_ACTION_TOAST_KEYS[action]));
        },
        onError: (err) => {
          if (err instanceof OpsApiError && err.code === 'RECEIPT_INVOICE_NOT_OPEN') {
            // Lost the decide race: the onSettled invalidation refreshes
            // the drawer to the true invoice state.
            setAction(null);
            return;
          }
          setActionError(coerceErrorMessage(err));
        },
      },
    );
  }

  const noteMissing = action === 'void' && note.trim().length === 0;

  return (
    <section className="flex flex-col gap-2" data-testid="receipt-detail-invoice">
      <div className="flex items-center justify-between gap-2">
        <h3 className="m-0 text-section text-paper">{t('receipts.invoice.title')}</h3>
        {canDecide && actionable ? (
          <div className="flex items-center gap-1.5">
            <Button
              size="sm"
              variant="primary"
              onClick={() => openAction('issue')}
              data-testid="receipt-invoice-issue"
            >
              {t('receipts.invoice.action.issue')}
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={() => openAction('void')}
              data-testid="receipt-invoice-void"
            >
              {t('receipts.invoice.action.void')}
            </Button>
          </div>
        ) : null}
        {canDecide && receipt.invoice_status === 'open' && receipt.status !== 'credited' ? (
          <span
            className="text-label text-paper-low"
            data-testid="receipt-invoice-await-credit"
          >
            {t('receipts.invoice.await_credit')}
          </span>
        ) : null}
      </div>
      <div className="grid gap-3 rounded-lg border border-line-soft bg-well/50 p-4 sm:grid-cols-2">
        <div className="flex flex-col gap-0.5">
          <span className="text-label text-paper-low">{t('receipts.invoice.status_label')}</span>
          <span>
            <Chip
              tone={receiptInvoiceStatusChipTone(receipt.invoice_status)}
              testId="receipt-invoice-status"
            >
              {t(RECEIPT_INVOICE_STATUS_LABEL_KEYS[receipt.invoice_status])}
            </Chip>
          </span>
        </div>
        <Fact
          label={t('receipts.invoice.mode_label')}
          value={
            receipt.invoice_mode !== null
              ? t(BRAND_INVOICE_MODE_LABEL_KEYS[receipt.invoice_mode])
              : '—'
          }
          testId="receipt-invoice-mode"
        />
        <Fact
          label={t('receipts.invoice.tax_point_label')}
          value={receipt.tax_point_bps !== null ? formatTaxPointBps(receipt.tax_point_bps) : '—'}
          mono
          testId="receipt-invoice-tax-point"
        />
        <Fact
          label={t('receipts.invoice.title_label')}
          value={receipt.invoice_title ?? '—'}
        />
        <Fact
          label={t('receipts.invoice.tax_no_label')}
          value={receipt.tax_no ?? '—'}
          mono={receipt.tax_no !== null}
        />
        <Fact
          label={t('receipts.invoice.decided_by')}
          value={<OpsUserRef id={receipt.issued_by_ops_user_id} />}
          mono={receipt.issued_by_ops_user_id !== null}
          testId="receipt-invoice-decided-by"
        />
        <Fact
          label={t('receipts.invoice.decided_at')}
          value={
            receipt.issued_at !== null ? formatLocalTimestamp(receipt.issued_at) : '—'
          }
        />
      </div>
      <Dialog
        open={action !== null}
        onOpenChange={(open) => {
          if (!open && !act.isPending) {
            setAction(null);
          }
        }}
        title={action !== null ? t(INVOICE_ACTION_TITLE_KEYS[action]) : ''}
        description={
          receipt.tax_point_bps !== null
            ? `${formatReceiptMinor(receipt.amount_minor, receipt.currency)} ${receipt.currency} · ${t('receipts.invoice.tax_point_label')} ${formatTaxPointBps(receipt.tax_point_bps)}`
            : `${formatReceiptMinor(receipt.amount_minor, receipt.currency)} ${receipt.currency}`
        }
        closeLabel={t('common.close')}
        testId="receipt-invoice-dialog"
        footer={
          <>
            <Button variant="ghost" onClick={() => setAction(null)} disabled={act.isPending}>
              {t('common.cancel')}
            </Button>
            <Button
              variant={action === 'issue' ? 'primary' : 'danger'}
              onClick={confirmAction}
              loading={act.isPending}
              disabled={noteMissing}
              data-testid="receipt-invoice-confirm"
            >
              {action !== null ? t(INVOICE_ACTION_LABEL_KEYS[action]) : ''}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <FormField
            label={t('receipts.decide.note_label')}
            fieldId="receipt-invoice-note"
            required={action === 'void'}
            hint={action === 'void' ? t('receipts.decide.note_required_hint') : undefined}
          >
            <Textarea
              id="receipt-invoice-note"
              value={note}
              onChange={(event) => setNote(event.currentTarget.value)}
              rows={3}
              data-testid="receipt-invoice-note"
            />
          </FormField>
          {actionError !== null ? (
            <p
              role="alert"
              data-testid="receipt-invoice-error"
              className="m-0 rounded-lg border border-bad-line bg-bad-dim/50 px-3 py-2 text-dense text-bad-bright"
            >
              {actionError}
            </p>
          ) : null}
        </div>
      </Dialog>
    </section>
  );
}

function TransitionTimeline({
  transitions,
}: {
  readonly transitions: readonly ReceiptOrderTransitionRecord[];
}): JSX.Element {
  const t = useT();
  return (
    <section className="flex flex-col gap-2" data-testid="receipt-detail-timeline">
      <h3 className="m-0 text-section text-paper">{t('receipts.detail.timeline.title')}</h3>
      <ol className="m-0 flex list-none flex-col gap-2 p-0">
        {transitions.map((step) => (
          <li key={step.id} className="flex flex-col gap-0.5">
            <div className="flex flex-wrap items-center gap-2">
              <Chip tone={receiptStatusChipTone(step.to_status)}>
                {step.from_status === null
                  ? t('receipts.detail.timeline.submitted')
                  : t(RECEIPT_STATUS_LABEL_KEYS[step.to_status])}
              </Chip>
              <span className="font-mono text-label text-paper-mid">
                <OpsUserRef id={step.actor_ops_user_id} />
              </span>
              <span className="font-mono text-label text-paper-low">
                {formatLocalTimestamp(step.created_at)}
              </span>
            </div>
            {step.note !== null && step.note.length > 0 ? (
              <p
                className="m-0 pl-1 text-dense text-paper-mid"
                data-testid="receipt-timeline-note"
              >
                {step.note}
              </p>
            ) : null}
          </li>
        ))}
      </ol>
    </section>
  );
}

function formatSize(sizeBytes: number): string {
  if (sizeBytes >= 1024 * 1024) {
    return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  if (sizeBytes >= 1024) {
    return `${(sizeBytes / 1024).toFixed(0)} KB`;
  }
  return `${sizeBytes} B`;
}

function AttachmentsSection({ receiptId }: { readonly receiptId: string }): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { fetchImpl } = useSession();
  const attachments = useReceiptAttachments(receiptId);
  const upload = useUploadReceiptAttachment();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  async function handleUpload(file: File): Promise<void> {
    if (isOversizeAttachment(file.size)) {
      toast.error(t('receipts.submit.error.oversize', { mb: attachmentMaxMbLabel() }));
      return;
    }
    const buffer = await file.arrayBuffer();
    upload.mutate(
      {
        receiptId,
        fileName: file.name,
        contentType: file.type.length > 0 ? file.type : 'application/octet-stream',
        dataBase64: bytesToBase64(new Uint8Array(buffer)),
      },
      {
        onSuccess: () => {
          toast.success(t('receipts.detail.attachments.toast.uploaded'));
        },
        onError: (err) => {
          toast.error(coerceErrorMessage(err));
        },
      },
    );
  }

  async function handleDownload(attachment: FinanceAttachmentSummary): Promise<void> {
    setDownloadingId(attachment.id);
    try {
      const payload = await downloadReceiptAttachment(receiptId, attachment.id, fetchImpl);
      const bytes = base64ToBytes(payload.data_base64);
      const blob = new Blob([bytes], { type: payload.attachment.content_type });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = payload.attachment.file_name;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(coerceErrorMessage(err));
    } finally {
      setDownloadingId(null);
    }
  }

  const rows = attachments.data?.attachments ?? [];

  return (
    <section className="flex flex-col gap-2" data-testid="receipt-detail-attachments">
      <div className="flex items-center justify-between gap-2">
        <h3 className="m-0 inline-flex items-center gap-1.5 text-section text-paper">
          <Paperclip aria-hidden="true" className="h-3.5 w-3.5" />
          {t('receipts.detail.attachments.title')}
        </h3>
        <Button
          size="sm"
          variant="secondary"
          loading={upload.isPending}
          onClick={() => fileInputRef.current?.click()}
          data-testid="receipt-attachment-upload"
        >
          <Upload aria-hidden="true" className="h-3.5 w-3.5" />
          {t('receipts.detail.attachments.upload')}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={(event) => {
            const file = event.currentTarget.files?.[0] ?? null;
            event.currentTarget.value = '';
            if (file !== null) {
              void handleUpload(file);
            }
          }}
          data-testid="receipt-attachment-file-input"
          aria-label={t('receipts.detail.attachments.upload')}
        />
      </div>
      <p className="m-0 text-micro text-paper-low">
        {t('receipts.detail.attachments.cap_hint', { mb: attachmentMaxMbLabel() })}
      </p>
      {attachments.isPending ? <Skeleton className="h-10 w-full" /> : null}
      {attachments.isError ? (
        <p className="m-0 text-dense text-bad" data-testid="receipt-attachments-error">
          {coerceErrorMessage(attachments.error)}
        </p>
      ) : null}
      {attachments.isSuccess && rows.length === 0 ? (
        <p className="m-0 text-dense text-paper-low" data-testid="receipt-attachments-empty">
          {t('receipts.detail.attachments.empty')}
        </p>
      ) : null}
      {rows.length > 0 ? (
        <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
          {rows.map((attachment) => (
            <li
              key={attachment.id}
              className="flex items-center justify-between gap-3 rounded-lg border border-line-soft bg-well/50 px-3 py-2"
              data-testid={`receipt-attachment-${attachment.id}`}
            >
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="truncate text-dense text-paper">{attachment.file_name}</span>
                <span className="font-mono text-micro text-paper-low">
                  {formatSize(attachment.size_bytes)} · {attachment.content_type} ·{' '}
                  <OpsUserRef id={attachment.uploaded_by_ops_user_id} /> ·{' '}
                  {formatLocalTimestamp(attachment.created_at)}
                </span>
              </div>
              <Button
                size="sm"
                variant="ghost"
                loading={downloadingId === attachment.id}
                onClick={() => void handleDownload(attachment)}
                data-testid={`receipt-attachment-download-${attachment.id}`}
              >
                <Download aria-hidden="true" className="h-3.5 w-3.5" />
                {t('receipts.detail.attachments.download')}
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
